import React, { useState } from 'react';
import { usePropFirmPresets, money, formatVerified } from '../../utils/propFirmPresets';
import MissingFirms from './MissingFirms';
import './PropFirmRulesTable.css';

/** Current prop firm rules (verified against each firm's own pages, re-checked weekly). */
function PropFirmRulesTable() {
  const { firms, live, notListed } = usePropFirmPresets();
  const [open, setOpen] = useState(() => (firms[0] ? firms[0].key : ''));

  return (
    <div className="pfr">
      <div className="pfr-tabs" role="tablist">
        {firms.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={open === f.key}
            className={`pfr-tab ${open === f.key ? 'active' : ''}`}
            onClick={() => setOpen(f.key)}
          >
            {f.firm}
          </button>
        ))}
      </div>
      {firms.filter((f) => f.key === open).map((f) => (
        <div key={f.key} className="pfr-firm" role="tabpanel">
          <div className="pfr-meta">
            <span className={`pfr-badge ${f.status === 'changed' || f.confirmed === false ? 'warn' : ''}`}>
              {f.confirmed === false ? '⚠ Not yet confirmed against the firm’s pages'
                : f.status === 'changed' ? '⚠ Possible rule change under review' : `✓ Verified ${formatVerified(f.verifiedAt)}`}
            </span>
            {!live && <span className="pfr-muted">Showing the built-in copy (couldn’t reach the server)</span>}
          </div>
          {f.plans.map((p) => (
            <div key={p.id} className="pfr-plan">
              <div className="pfr-plan-head">
                <h4>{p.plan}</h4>
                <span className="pfr-chip">{p.drawdownType}</span>
                <span className="pfr-chip pfr-chip-accent">Consistency {p.consistency}%</span>
              </div>
              <p className="pfr-note">{p.consistencyNote}</p>
              <div className="pfr-table-wrap">
                <table className="pfr-table">
                  <thead>
                    <tr><th>Account</th><th>Drawdown</th><th>Daily loss limit</th><th>Profit target</th><th>Max contracts</th></tr>
                  </thead>
                  <tbody>
                    {p.sizes.map((s) => (
                      <tr key={s.accountSize}>
                        <td>{s.label}</td>
                        <td>{money(s.drawdown)}</td>
                        <td>{s.dailyLossLimit ? money(s.dailyLossLimit) : 'None'}</td>
                        <td>{money(s.profitTarget)}</td>
                        <td>{s.maxContracts ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
          {f.rules?.consistency && <p className="pfr-detail"><strong>Consistency:</strong> {f.rules.consistency}</p>}
          {f.rules?.drawdown && <p className="pfr-detail"><strong>Drawdown:</strong> {f.rules.drawdown}</p>}
          {f.rules?.dailyLossLimit && <p className="pfr-detail"><strong>Daily loss limit:</strong> {f.rules.dailyLossLimit}</p>}
          <div className="pfr-sources">
            <span className="pfr-muted">Official pages:</span>
            {(f.sources?.length ? f.sources : [f.source]).filter(Boolean).slice(0, 4).map((src) => {
              const url = typeof src === 'string' ? src : src.url;
              if (!/^https:\/\//.test(url || '')) return null;
              let label = typeof src === 'string' ? '' : src.label;
              if (!label) { try { label = new URL(url).hostname.replace(/^www\./, ''); } catch (e) { label = url; } }
              return <a key={url} href={url} target="_blank" rel="noopener noreferrer nofollow">{label} ↗</a>;
            })}
          </div>
        </div>
      ))}
      <MissingFirms firms={notListed} className="pfr-missing" />
    </div>
  );
}

export default PropFirmRulesTable;
